import { getAttackPhase, getMvpCharacterDefinition } from '../../core';
import type { AttackPhase, BattleSnapshot, CharacterBattleSnapshot, RoundResult } from '../../core';

export type StickCharacterId = 'sales' | 'developer';
export const STICK_PRESENTATION_STATES = [
  'idle', 'walk', 'crouch', 'guard', 'jump', 'lightAttack', 'heavyAttack', 'airAttack',
  'skill1', 'skill2', 'super', 'hit', 'knockdown', 'wakeup', 'ko', 'win',
] as const;
export type StickPresentationState = (typeof STICK_PRESENTATION_STATES)[number];
export interface StickPoint { readonly x: number; readonly y: number }
export interface StickPose {
  readonly state: StickPresentationState;
  readonly attackPhase: AttackPhase | null;
  readonly head: StickPoint;
  readonly neck: StickPoint;
  readonly hip: StickPoint;
  readonly leftHand: StickPoint;
  readonly rightHand: StickPoint;
  readonly leftFoot: StickPoint;
  readonly rightFoot: StickPoint;
  readonly marker: 'NONE' | 'DOCUMENTS' | 'DEADLINE';
}
export interface StickVisualProfile {
  readonly id: StickCharacterId;
  readonly bodyColor: number;
  readonly accentColor: number;
  readonly lineWidth: number;
  readonly headRadius: number;
  readonly forwardLean: number;
  readonly armOpening: number;
  readonly stanceWidth: number;
  readonly centerHeight: number;
  readonly motionAmplitude: number;
}
export interface StickViewModel { readonly profile: StickVisualProfile; readonly pose: StickPose }

export const STICK_PROFILES: Readonly<Record<StickCharacterId, StickVisualProfile>> = Object.freeze({
  sales: Object.freeze({ id: 'sales', bodyColor: 0xfff3df, accentColor: 0xff8a3d, lineWidth: 9, headRadius: 20,
    forwardLean: 12, armOpening: 34, stanceWidth: 48, centerHeight: 112, motionAmplitude: 16 }),
  developer: Object.freeze({ id: 'developer', bodyColor: 0xe8f4ff, accentColor: 0x45a9ff, lineWidth: 8, headRadius: 18,
    forwardLean: 2, armOpening: 18, stanceWidth: 34, centerHeight: 98, motionAmplitude: 7 }),
});

const point = (x: number, y: number): StickPoint => Object.freeze({ x, y });
const phaseAmount = (phase: AttackPhase | null, startup: number, active: number, recovery: number): number =>
  phase === 'STARTUP' ? startup : phase === 'ACTIVE' ? active : phase === 'RECOVERY' ? recovery : 0;

export const selectStickState = (
  character: CharacterBattleSnapshot,
  roundResult: RoundResult | null,
  player: 0 | 1,
  waking = false,
): StickPresentationState => {
  if (character.state === 'KO') return 'ko';
  if (roundResult?.winner === player) return 'win';
  if (character.currentAttackId !== null) {
    const attack = getMvpCharacterDefinition(character.characterDefinitionId).attacks.find(({ id }) => id === character.currentAttackId);
    if (attack?.airOnly === true) return 'airAttack';
    if (attack?.attackType === 'SUPER') return 'super';
    if (attack?.usage === 'skill1' || attack?.usage === 'COUNTER_RESPONSE') return 'skill1';
    if (attack?.usage === 'skill2' || attack?.usage === 'TRAP') return 'skill2';
    if (attack?.usage === 'heavy') return 'heavyAttack';
    return 'lightAttack';
  }
  if (character.state === 'Knockdown') return waking ? 'wakeup' : 'knockdown';
  if (character.state === 'Hitstun') return 'hit';
  if (character.state === 'Guard') return 'guard';
  if (character.state === 'Crouch') return 'crouch';
  if (character.state === 'Jump' || !character.isGrounded) return 'jump';
  if (character.state === 'Walk' || Math.abs(character.velocity.x) > 0.01) return 'walk';
  return 'idle';
};

export const resolveAttackPhase = (character: CharacterBattleSnapshot): AttackPhase | null => {
  if (character.currentAttackId === null || character.attackElapsedFrames === null) return null;
  const definition = getMvpCharacterDefinition(character.characterDefinitionId).attacks.find(({ id }) => id === character.currentAttackId);
  return definition === undefined ? null : getAttackPhase(definition, character.attackElapsedFrames);
};

export const createStickPose = (
  profile: StickVisualProfile,
  state: StickPresentationState,
  attackPhase: AttackPhase | null,
  logicalFrame = 0,
): StickPose => {
  const sales = profile.id === 'sales'; const lean = profile.forwardLean; const center = profile.centerHeight;
  let head = point(lean, -center - 58); let neck = point(lean - 2, -center - 34); let hip = point(0, -38);
  let leftHand = point(-profile.armOpening, -center + 2); let rightHand = point(profile.armOpening + lean, -center + 4);
  let leftFoot = point(-profile.stanceWidth, 0); let rightFoot = point(profile.stanceWidth, 0); let marker: StickPose['marker'] = 'NONE';
  const cycle = logicalFrame % 24 < 12 ? 1 : -1;
  switch (state) {
    case 'walk': {
      const swing = profile.motionAmplitude * cycle; leftHand = point(-profile.armOpening + swing, -center + 4);
      rightHand = point(profile.armOpening + lean - swing, -center + 4); leftFoot = point(-profile.stanceWidth - swing, 0); rightFoot = point(profile.stanceWidth + swing, 0); break;
    }
    case 'crouch': head = point(lean, -112); neck = point(lean, -90); hip = point(0, -34); leftHand = point(-20, -70); rightHand = point(30, -72); leftFoot = point(-52, 0); rightFoot = point(52, 0); break;
    case 'guard': head = point(lean - 6, -center - 48); neck = point(lean - 5, -center - 25); leftHand = point(30, -center - 42); rightHand = point(42, -center - 12); leftFoot = point(-profile.stanceWidth, 0); rightFoot = point(profile.stanceWidth * 0.8, 0); break;
    case 'jump': head = point(lean, -center - 50); leftHand = point(-32, -center - 25); rightHand = point(40, -center - 28); leftFoot = point(-35, -42); rightFoot = point(38, -35); break;
    case 'hit': head = point(-profile.motionAmplitude, -center - 54); neck = point(-profile.motionAmplitude * 0.7, -center - 30); leftHand = point(-50 - profile.motionAmplitude, -center + 8); rightHand = point(15, -center + 18); break;
    case 'knockdown': case 'ko': head = point(-72, -24); neck = point(-45, -20); hip = point(22, -12); leftHand = point(-34, -2); rightHand = point(8, -34); leftFoot = point(62, 0); rightFoot = point(78, -20); break;
    case 'wakeup': head = point(-28, -78); neck = point(-17, -58); hip = point(10, -22); leftHand = point(-48, -22); rightHand = point(28, -45); leftFoot = point(-18, 0); rightFoot = point(54, 0); break;
    case 'win': if (sales) { leftHand = point(-70, -center - 75); rightHand = point(82, -center - 85); head = point(lean, -center - 64); } else { leftHand = point(-20, -center - 10); rightHand = point(35, -center - 50); } break;
    case 'lightAttack': {
      const extension = phaseAmount(attackPhase, -10, sales ? 88 : 70, sales ? 35 : 25); rightHand = point(extension, -center - 3); leftHand = point(-profile.armOpening, -center + 12); break;
    }
    case 'heavyAttack': {
      const extension = phaseAmount(attackPhase, -42, sales ? 125 : 155, sales ? 55 : 42); rightHand = point(extension, -center + (sales ? 14 : -2)); leftHand = point(-55, -center - 18); neck = point(lean + (attackPhase === 'ACTIVE' ? 24 : -8), -center - 32); if (!sales) marker = 'DOCUMENTS'; break;
    }
    case 'airAttack': rightHand = point(phaseAmount(attackPhase, -18, sales ? 105 : 88, 38), -center + 12); leftHand = point(-45, -center - 20); leftFoot = point(-40, -48); rightFoot = point(48, -22); break;
    case 'skill1': rightHand = point(phaseAmount(attackPhase, -18, sales ? 118 : 96, 38), -center - 8); leftHand = sales ? point(70, -center + 24) : point(32, -center - 42); break;
    case 'skill2': rightHand = point(sales ? 105 : 58, -center - 35); leftHand = point(-30, -center + 12); marker = sales ? 'DEADLINE' : 'NONE'; break;
    case 'super': leftHand = point(-82, -center - 52); rightHand = point(128, -center - 62); head = point(lean + 12, -center - 68); break;
    case 'idle': break;
  }
  return Object.freeze({ state, attackPhase, head, neck, hip, leftHand, rightHand, leftFoot, rightFoot, marker });
};

export const resolveStickView = (snapshot: BattleSnapshot, player: 0 | 1, waking = false): StickViewModel => {
  const character = snapshot.characters[player]; const id = character.characterDefinitionId as StickCharacterId;
  const profile = STICK_PROFILES[id]; const state = selectStickState(character, snapshot.roundResult, player, waking);
  return Object.freeze({ profile, pose: createStickPose(profile, state, resolveAttackPhase(character), snapshot.logicalFrame) });
};

export const mirrorStickPoint = (root: StickPoint, relative: StickPoint, facing: 'LEFT' | 'RIGHT'): StickPoint =>
  point(root.x + (facing === 'RIGHT' ? relative.x : -relative.x), root.y + relative.y);
